import { isValid } from 'date-fns'
import { Journey } from '@audibene/ta.utils-opportunity'
import { AppointmentType, Country } from '../../constants'
import { logFactory } from '../../util'
import { useOpportunities } from '../services/opportunities'
import { useAppointmentLocationState } from '../useAppointmentLocationState'
import { useCountry } from '../useCountry'
import { useIsUsTeleaudiologyContext } from '../useIsUsTeleaudiologyContext'
import { useNow } from '../useNow'
import { useSearchParameters } from '../useSearchParameters'
import { NO_APPOINTMENT_TYPES } from './trialGates'

const log = logFactory('hooks', 'useTrialGate')

const toStringParam = (value: string | string[] | undefined): string | undefined => (Array.isArray(value) ? value[0] : value)

/** Both trial fields arrive as server-serialized strings; absent and unparseable are treated alike. */
const toValidDate = (raw: string | null | undefined): Date | null => {
  const date = raw ? new Date(raw) : null

  return date && isValid(date) ? date : null
}

type TrialGateBlockedAppointmentType =
  | typeof AppointmentType.FOLLOW_UP
  | typeof AppointmentType.AFTERCARE_FOLLOW_UP
  | typeof AppointmentType.HEARING_AID_EVALUATION

// Which of the opportunity's two trial fields actually decided in/out of trial. Diagnostic only:
// no consumer should branch on this - it exists so analytics can tell "PX-3720 shipped and the
// primary path is engaging" apart from "PX-3720 shipped and every customer silently took the
// fallback" (e.g. because trialExpiration is null across the board).
type TrialStatusSource = 'trialExpiration' | 'trialEndDate'

type TrialGate = {
  // False outside US Teleaudiology, where the rule never runs at all. Distinguishes "the gate does
  // not apply to this customer" from "the gate applies and decided to block nothing", which are
  // otherwise identical from the outside.
  isInScope: boolean
  isLoading: boolean
  trialEndDate: Date | null
  // True when trial data is missing, invalid, or could not be fetched - the gate always
  // falls back to allowing every appointment type in this case (never blocks on bad data).
  isTrialDataUnknown: boolean
  // Every type this customer's trial status blocks. A set rather than a single value because one
  // trial decision can rule out more than one type: being out of trial blocks a fresh evaluation as
  // well as a standard follow-up (PX-3749). Empty whenever no decision was made.
  blockedAppointmentTypes: TrialGateBlockedAppointmentType[]
  // Absent when no decision was made (out of scope, loading, unknown data).
  trialStatusSource?: TrialStatusSource
}

// Out of trial, Aftercare Follow-up is the only thing left bookable: a standard follow-up would go
// back to the original provider, and a fresh evaluation restarts a journey this customer is past.
const OUT_OF_TRIAL_BLOCKED_TYPES: TrialGateBlockedAppointmentType[] = [AppointmentType.FOLLOW_UP, AppointmentType.HEARING_AID_EVALUATION]
const IN_TRIAL_BLOCKED_TYPES: TrialGateBlockedAppointmentType[] = [AppointmentType.AFTERCARE_FOLLOW_UP]

const INACTIVE_GATE: TrialGate = {
  isInScope: false,
  isLoading: false,
  trialEndDate: null,
  isTrialDataUnknown: false,
  blockedAppointmentTypes: NO_APPOINTMENT_TYPES,
}
// The two below are only ever returned after the in-scope check has passed.
const LOADING_GATE: TrialGate = {
  isInScope: true,
  isLoading: true,
  trialEndDate: null,
  isTrialDataUnknown: false,
  blockedAppointmentTypes: NO_APPOINTMENT_TYPES,
}
const UNKNOWN_TRIAL_DATA_GATE: TrialGate = {
  isInScope: true,
  isLoading: false,
  trialEndDate: null,
  isTrialDataUnknown: true,
  blockedAppointmentTypes: NO_APPOINTMENT_TYPES,
}

/**
 * Pure trial-status rule for gating Follow-up vs. Aftercare Follow-up scheduling (PX-1807/PX-3311).
 *
 * Fetches the customer's opportunity from opportunity-service and determines which of the two
 * follow-up appointment types should be blocked, from the trial boundary vs. now:
 *  - out of trial -> FOLLOW_UP and HEARING_AID_EVALUATION are blocked
 *  - in trial     -> AFTERCARE_FOLLOW_UP is blocked
 *  - unknown/missing/invalid/error, or outside the US Teleaudiology scope -> nothing is
 *    blocked (fallback-to-allow)
 *
 * The boundary comes from `trialExpiration` when available (PX-3720), falling back to the end of
 * `trialEndDate`'s calendar day in UTC (PX-1807) when it is not - see the comments on the
 * comparison itself for why both exist and why their strictness differs. An unusable
 * `trialExpiration` degrades to that fallback, *not* to unknown; an unusable `trialEndDate` is
 * still what makes the whole gate unknown.
 *
 * This hook has no feature-flag knowledge - see useBlockedAppointmentType for the flag-gated
 * result consumers should actually use.
 */
const useTrialGate = (): TrialGate => {
  const searchParams = useSearchParameters()
  const appointmentState = useAppointmentLocationState()
  const country = useCountry()

  const journey = (searchParams.journey as Journey | undefined) ?? (appointmentState?.journey as Journey | undefined)
  // opportunity_gid is always the opportunity's own Salesforce ID (a required URL/location-state
  // parameter for this whole scheduling flow) - fetch that exact opportunity directly instead of
  // filtering by customerGid, since real callers pass the *customer's* Salesforce ID as
  // customer_gid, which opportunity-service's customerGid filter does not match.
  const opportunitySalesforceId = toStringParam(searchParams.opportunity_gid) ?? appointmentState?.opportunity_gid
  // Server time, not the client's clock - matches useAcfuProgressiveDisclosure's convention for
  // other time-sensitive scheduling rules, so a skewed local clock can't misjudge trial status
  // right at the trial-end boundary. Stay in LOADING_GATE (rather than falling back to the local
  // clock) until this actually resolves, or a customer right at the boundary could be judged
  // against a skewed client clock and then flip once server time arrives.
  const { data: now, isLoading: isNowLoading } = useNow()

  const isInScope = useIsUsTeleaudiologyContext(journey, country)

  const {
    data: opportunities,
    isLoading,
    isError,
  } = useOpportunities(
    { salesforceId: opportunitySalesforceId, countryCode: country as Country, limit: 1 },
    { enabled: isInScope && Boolean(opportunitySalesforceId) }
  )

  if (!isInScope) return INACTIVE_GATE
  if (isLoading || isNowLoading) return LOADING_GATE

  // Settled without a value - the time service is unavailable. Treat it as unusable input and fail
  // open, rather than reporting "still loading" for the life of the mount: useNow has no fallback,
  // so that state never resolves, and a consumer that disables options while loading would leave
  // them dead with no tooltip and no spinner.
  if (!now) {
    log('⚠️ Server time unavailable for trial gate - failing open', { opportunitySalesforceId })
    return UNKNOWN_TRIAL_DATA_GATE
  }

  if (isError) {
    log('❌ Failed to fetch opportunity for trial gate', { opportunitySalesforceId })
    return UNKNOWN_TRIAL_DATA_GATE
  }

  const rawTrialEndDate = opportunities?.[0]?.trialEndDate
  const rawTrialExpiration = opportunities?.[0]?.trialExpiration
  const trialEndDate = toValidDate(rawTrialEndDate)
  const trialExpiration = toValidDate(rawTrialExpiration)

  // Still hinges on trialEndDate even though trialExpiration decides the boundary: blocking without
  // it would render the reason copy with an undefined date, so fail open instead. (That combination
  // should be impossible upstream - opportunity-service derives trialExpiration from trialEndDate.)
  if (!opportunities?.length || !trialEndDate) {
    log('⚠️ Trial data missing or invalid for trial gate', { opportunitySalesforceId, rawTrialEndDate, rawTrialExpiration })
    return UNKNOWN_TRIAL_DATA_GATE
  }

  if (rawTrialExpiration && !trialExpiration) {
    log('⚠️ trialExpiration is not a valid date - falling back to the trialEndDate rule', {
      opportunitySalesforceId,
      rawTrialExpiration,
    })
  }

  // Both boundaries are the same kind of value: the exclusive instant at which the trial is over,
  // so the comparison below is a single `>=` either way.
  //
  // trialExpiration is that instant exactly - midnight after the last trial day in the *customer's
  // own* timezone, computed server-side.
  //
  // The fallback derives it from trialEndDate, which arrives as a date-only value serialized at UTC
  // midnight (e.g. "2026-09-01T00:00:00.000Z"). Comparing that instant directly against `now` would
  // end the trial at the very start of its own end date - for any US timezone (behind UTC), "now"
  // is already past it for the entire calendar day the trial is supposed to still be valid through
  // - so advance to midnight of the following day (Date.UTC normalizes month/year overflow).
  //
  // That fallback is a deliberate approximation, not full customer-local-timezone correctness:
  // trialEndDate is a bare date with no timezone of its own, and the customer's timezone isn't
  // available at this call site. For a customer west of UTC it closes the "blocked a full day
  // early" defect but leaves a smaller residual - the trial can still end a few hours before that
  // customer's own local midnight (e.g. ~5pm Pacific on the last day, once it's already the next
  // day in UTC). trialExpiration closes that residual; it survives only on the fallback path.
  const trialExpiresAt =
    trialExpiration ?? new Date(Date.UTC(trialEndDate.getUTCFullYear(), trialEndDate.getUTCMonth(), trialEndDate.getUTCDate() + 1))

  const blockedAppointmentTypes = now >= trialExpiresAt ? OUT_OF_TRIAL_BLOCKED_TYPES : IN_TRIAL_BLOCKED_TYPES

  return {
    isInScope: true,
    isLoading: false,
    trialEndDate,
    isTrialDataUnknown: false,
    blockedAppointmentTypes,
    trialStatusSource: trialExpiration ? 'trialExpiration' : 'trialEndDate',
  }
}

export { useTrialGate }
export type { TrialGate, TrialGateBlockedAppointmentType, TrialStatusSource }
